/**
 * Gift Card Recipient Form JavaScript
 * Handles validation and error display for gift card recipient forms
 */

class GiftCardRecipientForm {
  constructor() {
    this.form = document.querySelector('[data-gift-card-recipient-form]');
    if (!this.form) return;
    
    this.init();
  }

  init() {
    this.setupValidation();
    this.setupDateValidation();
    this.setupFormSubmission();
  }

  setupValidation() {
    const emailInput = this.form.querySelector('#gift-card-recipient-email');
    const nameInput = this.form.querySelector('#gift-card-recipient-name');
    const messageInput = this.form.querySelector('#gift-card-message');
    const sendOnInput = this.form.querySelector('#gift-card-send-on');

    // Email validation
    if (emailInput) {
      emailInput.addEventListener('blur', () => this.validateEmail(emailInput));
      emailInput.addEventListener('input', () => this.clearError(emailInput));
    }

    // Name validation (optional but with length check)
    if (nameInput) {
      nameInput.addEventListener('blur', () => this.validateName(nameInput));
      nameInput.addEventListener('input', () => this.clearError(nameInput));
    }

    // Message validation (optional but with length check)
    if (messageInput) {
      messageInput.addEventListener('blur', () => this.validateMessage(messageInput));
      messageInput.addEventListener('input', () => this.clearError(messageInput));
    }

    // Send on date validation
    if (sendOnInput) {
      sendOnInput.addEventListener('change', () => this.validateSendOnDate(sendOnInput));
    }
  }

  setupDateValidation() {
    const sendOnInput = this.form.querySelector('#gift-card-send-on');
    if (!sendOnInput) return;

    // Set min date to today
    const today = new Date().toISOString().split('T')[0];
    sendOnInput.setAttribute('min', today);

    // Set max date to 90 days from today
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90);
    const maxDateString = maxDate.toISOString().split('T')[0];
    sendOnInput.setAttribute('max', maxDateString);
  }

  setupFormSubmission() {
    const productForm = this.form.closest('.product-form');
    if (!productForm) return;

    productForm.addEventListener('submit', (e) => {
      if (!this.validateForm()) {
        e.preventDefault();
        return false;
      }
    });
  }

  validateEmail(input) {
    const email = input.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) {
      this.showError(input, 'Email is required');
      return false;
    }
    
    if (!emailRegex.test(email)) {
      this.showError(input, 'Please enter a valid email address');
      return false;
    }
    
    this.clearError(input);
    return true;
  }

  validateName(input) {
    const name = input.value.trim();
    
    if (name && name.length > 255) {
      this.showError(input, 'Name cannot be longer than 255 characters');
      return false;
    }
    
    this.clearError(input);
    return true;
  }

  validateMessage(input) {
    const message = input.value.trim();
    
    if (message && message.length > 200) {
      this.showError(input, 'Message cannot be longer than 200 characters');
      return false;
    }
    
    this.clearError(input);
    return true;
  }

  validateSendOnDate(input) {
    const date = input.value;
    if (!date) return true; // Optional field
    
    const selectedDate = new Date(date);
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90);
    
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    maxDate.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      this.showError(input, 'Send date cannot be in the past');
      return false;
    }
    
    if (selectedDate > maxDate) {
      this.showError(input, 'Send date cannot be more than 90 days in the future');
      return false;
    }
    
    this.clearError(input);
    return true;
  }

  validateForm() {
    const emailInput = this.form.querySelector('#gift-card-recipient-email');
    const nameInput = this.form.querySelector('#gift-card-recipient-name');
    const messageInput = this.form.querySelector('#gift-card-message');
    const sendOnInput = this.form.querySelector('#gift-card-send-on');

    let isValid = true;

    // Validate required email
    if (emailInput && !this.validateEmail(emailInput)) {
      isValid = false;
    }

    // Validate optional fields
    if (nameInput && !this.validateName(nameInput)) {
      isValid = false;
    }

    if (messageInput && !this.validateMessage(messageInput)) {
      isValid = false;
    }

    if (sendOnInput && !this.validateSendOnDate(sendOnInput)) {
      isValid = false;
    }

    return isValid;
  }

  showError(input, message) {
    const errorElement = input.parentNode.querySelector('.gift-card-recipient-form__error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('show');
      input.setAttribute('aria-invalid', 'true');
    }
  }

  clearError(input) {
    const errorElement = input.parentNode.querySelector('.gift-card-recipient-form__error');
    if (errorElement) {
      errorElement.classList.remove('show');
      input.removeAttribute('aria-invalid');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new GiftCardRecipientForm();
});

// Also initialize for dynamic content (like quick buy)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-gift-card-recipient-form]')) {
    new GiftCardRecipientForm();
  }
}); 